'use client';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import {styled} from '@mui/material/styles';
import type {FC, ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import type {UnitSpecView} from '../../model';
import {RichContent} from '../article';
import {DownloadBrochureButton} from '../brochure';
import {Section, SectionTitle} from '../primitives';
import {UnitSpecList} from './UnitSpecList';

export interface UnitAboutSectionProps {
  description?: string;
  specs: UnitSpecView[];
  brochureUrl?: string;
  name: string;
  onDownloadBrochure: () => void;
}

const Layout = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(4),
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    gap: theme.spacing(8),
  },
})) as FC<{children?: ReactNode}>;

const Body = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(2.5),
  flex: '1 1 0',
  minWidth: 0,
})) as FC<{children?: ReactNode}>;

export function UnitAboutSection({description, specs, brochureUrl, name, onDownloadBrochure}: UnitAboutSectionProps) {
  const {t} = useTranslation();

  return (
    <Section tone="raised">
      <Container>
        <SectionTitle as="h2" sx={{mb: 1}}>
          {t('about_property')}
        </SectionTitle>
        <Layout>
          <Body>
            {description && <RichContent html={description} />}
            {brochureUrl && <DownloadBrochureButton url={brochureUrl} name={name} onDownload={onDownloadBrochure} />}
          </Body>
          <UnitSpecList specs={specs} />
        </Layout>
      </Container>
    </Section>
  );
}

UnitAboutSection.displayName = 'UnitAboutSection';
